import { registerSW } from 'virtual:pwa-register';
import type RootStore from './stores/RootStore';
import type ServiceStore from '@intrasync/stores/core/ServiceStore';
import {
  applyUpdateWhenHidden,
  clearPreloadErrorGuard,
  installPreloadErrorRecovery,
} from './pwaUpdatePolicy';

export function permissionRequest() {
  return new Promise(function (resolve, reject) {
    const permissionResult = Notification.requestPermission(function (result) {
      resolve(result);
    });

    if (permissionResult) {
      permissionResult.then(resolve, reject);
    }
  })
    .then(function (permissionResult) {
      if (permissionResult !== 'granted') {
        // Permission not granted
      }
    })
    .catch(() => {
      // Permission request error
    });
}

/**
 * Registers the service worker.
 *
 * `vite.config.ts` uses `registerType: 'prompt'`. It used to be `'autoUpdate'`
 * with `skipWaiting` + `clientsClaim`, which made vite-plugin-pwa reload every
 * open tab the instant a new worker activated. That is what put a user on the
 * sign-in form back at a blank sign-in form mid-deploy, discarding typed
 * credentials — the server saw no login attempt at all because the submit
 * never happened. `onNeedRefresh` was also dead in that mode: the plugin only
 * calls it in its `prompt` branch, so the `setAppUpdateFound` wiring below
 * could never fire.
 *
 * Now the update is announced (for a shell to render) and applied only once the
 * tab is hidden, so nobody is interrupted. See `pwaUpdatePolicy.ts`.
 */
export const pwaRegistration = (rootStore: RootStore) => {
  try {
    installPreloadErrorRecovery();

    const updateSW = registerSW({
      onNeedRefresh() {
        if (rootStore?.viewStore) {
          rootStore.viewStore?.setAppUpdateFound(true);
        }
        applyUpdateWhenHidden(updateSW);
      },
      onOfflineReady() {
        // Offline ready
      },
      onRegisteredSW(swPath: string, registration: ServiceWorkerRegistration) {
        if (rootStore?.viewStore) {
          rootStore.viewStore?.setSwRegistration(registration);
        }
      },
      onRegisterError(err: unknown) {
        // Handle InvalidStateError gracefully in development
        if (err instanceof Error && err.name === 'InvalidStateError') {
          return;
        }
        if (rootStore?.logStore) {
          const logStore = rootStore.logStore as ServiceStore;
          logStore
            .create({
              message: 'Error Creating Service Worker',
              level: 'error',
              payload: err,
            })
            .catch(() => {
              // Log creation error
            });
        }
      },
    });
    if (rootStore?.viewStore && updateSW) {
      rootStore.viewStore.setSWUpdater(updateSW);
    }
    // Registration succeeded, so the app booted far enough that any earlier
    // preload-error reload did its job.
    clearPreloadErrorGuard();
    return 'Successfully Registered';
  } catch {
    // Service worker registration error
  }
};
